/**
 * ChatBackendInterface implementation for the agentic-chat backend
 * (issue #8147). Targets the new endpoints under /chat/conversations
 * and the WebSocket room ``chat_conversation_<conversation_id>_default``.
 *
 * Behavior parity with MainChatBackend (the legacy backend) where the
 * concepts overlap:
 *   - createWorkspace → POST /chat/conversations, returns conversation_id.
 *   - loadWorkspace   → GET /chat/conversations/{id}, folds the event
 *                       stream into ChatMessageDto[] (one user + one
 *                       assistant per turn along the active branch).
 *   - sendMessage     → POST /chat/conversations/{id}/messages.
 *                       preceding_query_id (legacy) maps to
 *                       branch_from_turn_id (new).
 *   - cancelMessage   → POST /chat/conversations/{id}/turns/{turn_id}/cancel.
 *                       Requires conversation_id; we keep it from the
 *                       last loadWorkspace / createWorkspace call.
 *   - parseWebsocketMessage → consumes ``CHAT_EVENT`` frames; maps the
 *                       inner ``kind`` to the legacy ChatUpdate shape so
 *                       the reducer in ChatConversation needs no changes.
 *
 * Citations use the legacy ``sourceMap`` shape (keyed by the bracketed
 * ``[NNNNN.M]`` marker) attached to the final assistant_text event, resolved
 * by MarkDownRenderer's remarkChatCitations path.
 */

import { BASE_API_URL_V2 } from "../../../constants";
import fpFetch from "../../../services/fpFetch";
import { rememberSentImages, rememberedImageUrl } from "./chatImageAttachments";
import { currentViewerTimeZone } from "@/utils/viewerTimeZone";
import { WeeklyLimitReachedError } from "../WeeklyUsage/weeklyLimit";

import {
  ChatBackendInterface,
  ChatUpdate,
  ChatWebsocketRoom,
  RefusedAgentFile,
  SendMessageParams,
} from "./ChatBackendInterface";
import {
  fromConversationSlug,
  RenderedChart,
  renderChartViaProxy,
  toConversationSlug,
} from "./chatUtils";
import {
  buildTurn,
  closeOutTurn,
  markBuildEndedAfterTurn,
  TurnState,
} from "./agentic/segments";
import { isEntityKind } from "./components/mentionTokens";
import { attachmentRequestFields } from "./composerSelection";
import {
  ChartArtifact,
  ChatCitationSource,
  ChatMessageDto,
  AttachedImageRef,
  ChatDocumentComment,
  ReferencedEntity,
  SelectedFile,
  ToolStep,
} from "./types";

const API_BASE = `${BASE_API_URL_V2}/api/v2`;

// A sequence past any real event, so the conversation endpoint returns turn
// rows with an empty event stream (#11525).
export const MAX_EVENT_SEQ = 999999999;

// `chat_turns.status` values a turn can no longer leave. Distinct from
// ChatMessageStatus — this is the wire vocabulary, which spells them
// "complete"/"cancelled".
const TERMINAL_TURN_STATUSES_WIRE: readonly NonNullable<
  ChatTurnRefDto["status"]
>[] = ["complete", "failed", "cancelled"];

// True only for an id the backend actually assigned to a turn. Optimistic ids
// minted client-side are bare uuids, so naming one as a turn's parent violates
// the chat_turns self-FK and 500s the send (#10665).
const isPersistedTurnId = (id: string | null | undefined): id is string =>
  typeof id === "string" && id.startsWith("pgid-chat-turns-");

interface ConversationResponse {
  conversation_id: string;
  organization_id: string;
  owner_user_id: string;
  title: string | null;
  status: string;
  runtime_session_id: string | null;
  attached_blob_ids: string[];
  head_turn_id: string | null;
}

/** The persisted event vocabulary, shared by chat and the agents view and
 * defined by `fp/agent/conversation/unified_events.py`. The streaming-only
 * `heartbeat` kind never has a row and so never appears on a persisted
 * event. */
export type AgentEventType =
  | "user_message"
  | "assistant_text"
  | "assistant_thinking"
  | "tool_call"
  | "tool_result"
  | "research_packet"
  | "clarifying_question"
  | "clarifying_answer"
  | "system_event"
  | "error";

/**
 * ONE persisted `chat_events` row as the backend serves it — the single
 * client-bound event shape both products carry since #11155
 * (`ChatEventEnvelope` in `fp/agent/conversation/envelope.py`): chat's reload
 * payloads and the agents view's session/websocket payloads are the same
 * rows. `turn_id` is null only for a pre-flip agents row that predates
 * per-turn persistence. `parent_event_id` (runtime sub-event nesting) may be
 * null; `created_at` is the row's insert time and is populated on chat rows
 * — the reloaded evidence footer's as-of reads it (#11426).
 */
// The fields a user-authored event (`user_message`, `clarifying_answer`)
// carries on its payload, declared so both transcripts read them without
// casting; every other event type's fields stay an open bag below.
export interface UserEventPayload {
  content?: string;
  answer?: string;
  // Who sent this message (#11173). `sender_name` is resolved server-side, so
  // the UI renders the name it was given rather than looking users up. Both
  // null for messages recorded before senders were tracked.
  sender_user_id?: string | null;
  sender_name?: string | null;
  // The turn this message stopped to send itself (Send now, #11986); the
  // fold hides that turn's interrupted banner.
  superseded_turn_id?: string | null;
  // What the run page's task panel showed when the message was sent from
  // it (#11942 round 13); the panel shows only the turns that carry it.
  // Null or absent for a message sent from the whole-run view or from chat.
  viewing?: string | null;
}

export interface ChatEventDto {
  event_id: string;
  conversation_id: string;
  turn_id: string | null;
  seq: number;
  kind: AgentEventType;
  // Payloads are per-event-type, so they stay an open bag; the user-authored
  // fields above are the ones the transcripts read by name.
  payload: UserEventPayload & Record<string, unknown>;
  is_user_visible: boolean;
  parent_event_id?: string | null;
  created_at?: string | null;
}

export interface ChatTurnRefDto {
  turn_id: string;
  // The edit/branch parent (the legacy prev_query_id). Null for the root turn.
  parent_turn_id: string | null;
  // Live turn status. Optional so payloads predating #10442 degrade to
  // event-derived statuses.
  status?: "pending" | "running" | "complete" | "failed" | "cancelled";
}

export interface ConversationDetailResponse {
  conversation: ConversationResponse;
  events: ChatEventDto[];
  turns: ChatTurnRefDto[];
}

interface SendMessageResponse {
  turn_id: string;
  user_message_event: ChatEventDto;
}

interface InFlightConflict {
  error: "turn_in_flight";
  in_flight_turn_id: string;
}

// Mirrors WeeklyLimitReachedResponse in fp/routes/chat.py (#13284).
interface WeeklyLimitRefusal {
  error: "weekly_limit_reached";
  message: string;
  next_reset: string | null;
}

export class AgenticChatBackend implements ChatBackendInterface {
  readonly kind = "agentic" as const;

  // The cancel endpoint is conversation-scoped, but cancelMessage(messageId)
  // gets only a turn id from the caller. Cache the conversation we're
  // currently driving so cancel can resolve the URL.
  //
  // `protected` so a surface that only differs in how a conversation is
  // CREATED can override that one method and still record what it created
  // (#11205) — everything downstream of creation is shared.
  protected currentConversationId: string | null = null;

  // --- workspace ops -----------------------------------------------------

  async createWorkspace(): Promise<string> {
    const response = await fpFetch(`${API_BASE}/chat/conversations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attached_blob_ids: [] }),
    });
    if (!response.ok) {
      throw new Error(`Failed to create conversation: ${response.status}`);
    }
    const conv = (await response.json()) as ConversationResponse;
    this.currentConversationId = conv.conversation_id;
    return conv.conversation_id;
  }

  async loadWorkspace(workspaceId: string): Promise<{
    messages: ChatMessageDto[];
    title: string;
    chartDataMap?: Record<string, Record<string, any>>;
    migrated_to?: string;
  }> {
    const response = await fpFetch(
      `${API_BASE}/chat/conversations/${workspaceId}`
    );
    if (response.status === 404 || response.status === 403) {
      throw new Error("WORKSPACE_NOT_FOUND");
    }
    if (!response.ok) {
      throw new Error(`Failed to load conversation: ${response.status}`);
    }
    const detail = (await response.json()) as ConversationDetailResponse;
    this.currentConversationId = detail.conversation.conversation_id;
    const messages = foldEventsToMessages(detail.events, detail.turns);
    return {
      messages,
      title: detail.conversation.title || "New Chat",
      chartDataMap: undefined,
    };
  }

  /**
   * Whether the given turn has reached a terminal status (#11525).
   *
   * Same endpoint as `loadWorkspace`, but `since_seq` past the end drops the
   * event stream from the payload while `turns` — the authoritative live
   * status (#10442) — still comes back in full. Measured on a two-turn
   * conversation: 64.7 kB folded vs 941 B here, and the gap widens with every
   * turn. That difference is what makes it affordable to poll for a missed
   * completion at all.
   *
   * A turn that is absent is reported unsettled on purpose: it is the
   * optimistic window right after a send, not a finished turn.
   */
  async isTurnSettled(workspaceId: string, turnId: string): Promise<boolean> {
    const response = await fpFetch(
      `${API_BASE}/chat/conversations/${workspaceId}?since_seq=${MAX_EVENT_SEQ}`
    );
    if (!response.ok) {
      throw new Error(`Failed to probe turn status: ${response.status}`);
    }
    const detail = (await response.json()) as ConversationDetailResponse;
    const turn = detail.turns.find((t) => t.turn_id === turnId);
    return turn?.status !== undefined
      ? TERMINAL_TURN_STATUSES_WIRE.includes(turn.status)
      : false;
  }

  async updateWorkspaceTitle(
    workspaceId: string,
    title: string
  ): Promise<void> {
    const response = await fpFetch(
      `${API_BASE}/chat/conversations/${workspaceId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to rename conversation: ${response.status}`);
    }
  }

  // The copy is titled server-side, so unlike the legacy backend this needs no
  // follow-up rename call (#10499).
  async cloneWorkspace(workspaceId: string): Promise<string> {
    const response = await fpFetch(
      `${API_BASE}/chat/conversations/${workspaceId}/clone`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to copy conversation: ${response.status}`);
    }
    const conv = (await response.json()) as ConversationResponse;
    return conv.conversation_id;
  }

  // --- message ops -------------------------------------------------------

  async sendMessage(params: SendMessageParams): Promise<string | void> {
    const conversationId = params.workspaceId;
    this.currentConversationId = conversationId;
    const body: Record<string, any> = {
      content: params.message,
      // The per-message thinking effort level (#12389). Omitted when the
      // user chose none; the backend then runs the turn at Medium.
      ...(params.options.thinking_effort
        ? { thinking_effort: params.options.thinking_effort }
        : {}),
      // The asker's timezone, resolved from the browser on every send rather
      // than stored on the user: a person who flies somewhere else asks their
      // next question from the new zone, and a stored preference would be
      // stale on landing. The turn's system prompt states this alongside the
      // organization's zone, so "what did I do today" is answered on the
      // asker's calendar instead of the server's UTC one. Same field name the
      // RMS grids already send. (#11055)
      user_tz: currentViewerTimeZone(),
    };
    // Attached documents (#11207): authorized server-side and unioned onto the
    // conversation's attached set. The ids are always sent, an empty list
    // included, so a message with nothing attached says so; the folder fields
    // let the transcript show a folder pick as the folder.
    Object.assign(
      body,
      attachmentRequestFields(
        params.selectedFiles,
        params.selectedFolderPaths ?? []
      )
    );
    // The tool the user picked in the input bar for this message. Persisted on
    // the user_message event so the turn driver can strongly encourage (not
    // force) its use, and so a reloaded conversation shows the tool chip. Omit
    // when nothing was selected. (#8147)
    if (params.options.isWebSearch) {
      body.selected_tool = "web";
    }
    // The skill the user slash-invoked for this message (#9789): validated
    // server-side and pinned for this turn's materialization only. Omit when
    // no skill was invoked.
    if (params.options.skill_id) {
      body.skill_id = params.options.skill_id;
    }
    // The records the user @-mentioned (#12154), as the {kind, id} pairs the
    // route validates; it stores the database's name for each. Omit when
    // none.
    if (params.options.referenced_entities?.length) {
      body.referenced_entities = params.options.referenced_entities.map(
        (entity) => ({ kind: entity.kind, id: entity.id })
      );
    }
    // The turn this message stopped to send itself (Send now, #11986). Only
    // a real persisted turn can be named; the optimistic id of a turn the
    // server never recorded would be a 422.
    if (isPersistedTurnId(params.options.superseded_turn_id)) {
      body.superseded_turn_id = params.options.superseded_turn_id;
    }
    // The id the message was minted with (#13890). It is in `body`, so the
    // cancel-and-resend below repeats it: that resend is the same message.
    if (params.options.client_message_id) {
      body.client_message_id = params.options.client_message_id;
    }
    // Screenshots attached to this message (#11382). Base64 inline rather than
    // a prior upload call, because these never become documents — there is no
    // blob to create. Omitted when none, so the common send is unchanged.
    if (params.images?.length) {
      body.images = params.images.map((image) => ({
        name: image.name,
        media_type: image.mediaType,
        data_b64: image.dataB64,
      }));
    }
    // Edit-user-message: branch from the parent turn verbatim. `branch: true`
    // lets the backend pin parent_turn_id even when it's null (a root sibling),
    // which `branch_from_turn_id or head` couldn't express. preceding_query_id
    // is the edited turn's parent (undefined ⇒ root ⇒ null parent).
    if (params.options.isBranch) {
      const parent = params.options.preceding_query_id ?? null;
      // Branch only from a real persisted turn (or the conversation root,
      // null). A stale/optimistic parent id — one that never reconciled to a
      // pgid-chat-turns-* turn — would FK-violate, so fall back to a linear
      // append rather than crash. (#8147)
      if (parent === null || isPersistedTurnId(parent)) {
        body.branch = true;
        body.branch_from_turn_id = parent;
      }
    } else if (isPersistedTurnId(params.options.preceding_query_id)) {
      // Same rule on the linear path: only a real turn may be named as the
      // parent. Omitting it is not a fallback — it is what a linear append
      // means, and the backend then parents the turn to its own head_turn_id,
      // the authoritative branch leaf. (#10665)
      body.branch_from_turn_id = params.options.preceding_query_id;
    }
    for (const [key, value] of Object.entries(this.extraMessageFields())) {
      if (!(key in body)) body[key] = value;
    }
    try {
      return await this.postMessage(conversationId, body);
    } catch (err) {
      if (!(err instanceof InFlightTurnError)) {
        throw err;
      }
      // Stop-then-send recovery (#10533). A 409 means a turn is still in
      // flight — typically one the user just Stopped, which stays 'running'
      // while it cooperatively tears down. Cancel it (idempotent if already
      // cancelled) and resend once: cancelling sets cancellation_requested, so
      // the backend supersedes that turn and admits this send instead of
      // conflicting again. A second conflict is left to propagate rather than
      // looping.
      await this.cancelMessage(err.inFlightTurnId);
      return await this.postMessage(conversationId, body);
    }
  }

  /**
   * Fields a hosting surface adds to every message it sends (#12172). The
   * notetaker's chat page says where the user is standing
   * (`notetaker_context`) while everything else about the send, and the whole
   * conversation surface, stays the main chat's. Added, never replacing: a
   * key the send already set wins. Nothing by default.
   */
  protected extraMessageFields(): Record<string, unknown> {
    return {};
  }

  // POST the assembled message body once. Throws InFlightTurnError on a 409 so
  // sendMessage can run the cancel-and-resend recovery above, and
  // WeeklyLimitReachedError on a 402 for the conversation to state.
  private async postMessage(
    conversationId: string,
    body: Record<string, any>
  ): Promise<string | void> {
    const response = await fpFetch(
      `${API_BASE}/chat/conversations/${conversationId}/messages`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        // Read here rather than reported as a failure: the refusal at the
        // weekly usage limit carries the instant it lifts (#13284).
        expectedStatuses: [402],
      }
    );
    if (response.status === 409) {
      const conflict = (await response.json()) as InFlightConflict;
      throw new InFlightTurnError(conflict.in_flight_turn_id);
    }
    if (response.status === 402) {
      // The organization reached its weekly usage limit (#13284); nothing was
      // written. The refusal names when new messages are admitted again.
      const refusal = (await response.json()) as WeeklyLimitRefusal;
      throw new WeeklyLimitReachedError(refusal.next_reset ?? null);
    }
    if (!response.ok) {
      throw new Error(`Failed to send message: ${response.status}`);
    }
    const data = (await response.json()) as SendMessageResponse;
    // Hold on to the bytes we just sent, under the ids the server assigned
    // them. Moments from now this message is rebuilt from server events and
    // its images become refs; without this the screenshot would blink out
    // while it was fetched back from the server that just received it.
    const sentImages = body.images as
      | { name: string; media_type: string; data_b64: string }[]
      | undefined;
    if (sentImages?.length) {
      const refs = data.user_message_event?.payload?.images;
      if (Array.isArray(refs)) {
        rememberSentImages(
          refs.map(
            (ref: { attachment_id?: string }) => ref?.attachment_id ?? ""
          ),
          sentImages.map((image) => ({
            name: image.name,
            mediaType: image.media_type,
            dataB64: image.data_b64,
          }))
        );
      }
    }
    // Returning the turn_id lets ChatConversation reconcile its
    // optimistic message id with the backend-assigned one.
    return data.turn_id;
  }

  async cancelMessage(messageId: string): Promise<void> {
    if (!this.currentConversationId) {
      console.warn(
        "AgenticChatBackend.cancelMessage called before any conversation loaded"
      );
      return;
    }
    await fpFetch(
      `${API_BASE}/chat/conversations/${this.currentConversationId}/turns/${messageId}/cancel`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      }
    );
  }

  /**
   * Deletes a turn nothing is built on (#12426). Agentic messages are
   * addressed by turn id, so `turnId` is the turn's id, not a message row's.
   * The server refuses a turn that has a follow-up or is still being answered;
   * the error then carries its own reason.
   */
  async deleteMessage(turnId: string): Promise<void> {
    if (!this.currentConversationId) {
      throw new Error("No conversation is open");
    }
    const response = await fpFetch(
      `${API_BASE}/chat/conversations/${this.currentConversationId}/turns/${turnId}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as {
        detail?: unknown;
      } | null;
      throw new Error(
        typeof body?.detail === "string"
          ? body.detail
          : "Could not delete the message"
      );
    }
  }

  // --- websocket parser --------------------------------------------------

  // Streaming text arrives as deltas from the runtime, but the chat
  // reducer's UPDATE_CONTENT replaces the message content wholesale (legacy
  // MainChatBackend sent cumulative text). Accumulate per turn here so the
  // reducer keeps working unchanged; cleared on the matching is_final or
  // turn_complete event.
  private streamBuffers = new Map<string, string>();
  private thinkingBuffers = new Map<string, string>();

  parseWebsocketMessage(message: unknown): ChatUpdate | null {
    try {
      const update = parseChatEvent(message);
      if (update === null) return null;

      if (update.type === "streaming") {
        const accumulated =
          (this.streamBuffers.get(update.messageId) ?? "") + update.text;
        this.streamBuffers.set(update.messageId, accumulated);
        return { ...update, text: accumulated };
      }

      if (update.type === "thinking") {
        if (!update.parentToolCallId) {
          // Top-level thinking frames arrive CUMULATIVE from the turn
          // driver (#10396) — a dropped transient frame self-heals on the
          // next one instead of leaving a hole in the dialogue.
          return update;
        }
        const accumulated =
          (this.thinkingBuffers.get(update.messageId) ?? "") + update.text;
        this.thinkingBuffers.set(update.messageId, accumulated);
        return { ...update, text: accumulated };
      }

      if (
        update.type === "contentWithCitations" ||
        update.type === "finalAnswer"
      ) {
        // Final text replaces whatever we accumulated; drop both buffers
        // so a follow-up turn under the same id (unlikely but cheap)
        // starts clean.
        this.streamBuffers.delete(update.messageId);
        this.thinkingBuffers.delete(update.messageId);
      }

      if (update.type === "toolCall" || update.type === "thinkingNote") {
        // A tool call or the run's persisted note ends the thinking RUN —
        // the next run must start a fresh live row, not carry this run's
        // text (#10396).
        this.thinkingBuffers.delete(update.messageId);
      }

      if (update.type === "status" && update.status !== "started") {
        this.streamBuffers.delete(update.messageId);
        this.thinkingBuffers.delete(update.messageId);
      }

      return update;
    } catch (error) {
      console.error("Error parsing agentic-chat websocket message:", error);
      return null;
    }
  }

  // --- charting ----------------------------------------------------------

  async renderChart(
    chartData: any,
    width: number,
    height: number
  ): Promise<RenderedChart> {
    return renderChartViaProxy(chartData, width, height);
  }

  getWebsocketRoom(workspaceId: string): ChatWebsocketRoom {
    // workspaceId is the chat conversation id when this backend is in use.
    return {
      kind: "chat_conversation",
      id: workspaceId,
      discriminator: "default",
    };
  }

  // The agentic conversation id is the pgid; the URL shows the bare uuid.
  idFromSlug(slug: string): string {
    return fromConversationSlug(slug);
  }

  slugFromId(id: string): string {
    return toConversationSlug(id);
  }
}

export class InFlightTurnError extends Error {
  public inFlightTurnId: string;
  constructor(inFlightTurnId: string) {
    super(
      "A turn is already in flight for this conversation. Cancel it before sending another message."
    );
    this.name = "InFlightTurnError";
    this.inFlightTurnId = inFlightTurnId;
  }
}

// ---------------------------------------------------------------------------
// Pure helpers (exported for testing)
// ---------------------------------------------------------------------------

// The agent's charting tool, namespaced by its MCP server (``mcp__<server>__
// <tool>``). It lives on the hosted Finpilot MCP server, which the runtime
// mounts under the ``finpilot`` key — hence ``mcp__finpilot__create_chart``. A
// tool_call with this name carries a Chart.js spec in its input and is surfaced
// as an inline chart rather than a generic tool step (#8774).
export const CHART_TOOL_NAME = "mcp__finpilot__create_chart";

// The agent's diagram tool, namespaced by its MCP server. Like create_chart it
// lives on the hosted Finpilot MCP server and is surfaced as an inline diagram
// (Mermaid source in its input) rather than a generic tool step (#10250).
export const DIAGRAM_TOOL_NAME = "mcp__finpilot__create_diagram";

// The agent's in-flight answer verifier, namespaced like the two above. It is
// an ordinary work step in the trace, but it is transparent for placing a
// chart or diagram: a drawing followed only by verification stands with the
// answer, and a text the model typed before the call that is the draft it
// submitted (``draft_answer`` in the call's input) renders nothing once the
// verified final replaces it (#12105).
export const VERIFY_TOOL_NAME = "mcp__finpilot__verify_answer";

/** A chart's source id as the bracketed marker the turn's map is keyed by
 *  (#11975): a Source Number, bare or bracketed. Anything else — the retired
 *  ``doc:`` / ``rms:`` ids persisted tool calls may still carry — resolves
 *  to nothing and is dropped. */
export const chartSourceMarker = (raw: string): string | null => {
  const marker = raw.trim().match(/^\[?(\d{4,7}\.\d{1,6})\]?$/);
  return marker ? `[${marker[1]}]` : null;
};

/**
 * Pull the sources off a create_chart / create_diagram tool input, as the
 * bracketed markers the turn's source map resolves (#11975). ``sources`` is
 * the argument's name; ``source_ids`` is what tool calls persisted before
 * the rename carry. Returns undefined when absent so an artifact with no
 * sources carries no empty array.
 */
function chartSourceIds(input: unknown): string[] | undefined {
  if (!input || typeof input !== "object") {
    return undefined;
  }
  const record = input as Record<string, unknown>;
  const raw = record.sources ?? record.source_ids;
  if (!Array.isArray(raw)) {
    return undefined;
  }
  const ids: string[] = [];
  for (const item of raw) {
    const marker = typeof item === "string" ? chartSourceMarker(item) : null;
    if (marker && !ids.includes(marker)) ids.push(marker);
  }
  return ids.length > 0 ? ids : undefined;
}

/**
 * Pull the chart artifacts a turn produced from its parsed ChatUpdates — the
 * same `chart` updates parseChatEvent emits for the live stream, so reload
 * and live share the one implementation of the create_chart mapping (#8774,
 * #11155). Returns undefined when the turn rendered no charts so the
 * assistant message carries no empty chart area.
 */
function chartsFromUpdates(updates: ChatUpdate[]): ChartArtifact[] | undefined {
  const charts: ChartArtifact[] = [];
  for (const u of updates) {
    if (u.type === "chart") {
      charts.push({
        id: u.chartId,
        spec: u.spec,
        title: u.title,
        sourceIds: u.sourceIds,
      });
    }
  }
  return charts.length > 0 ? charts : undefined;
}

/**
 * The screenshots a user message was sent with, as persisted on its
 * user_message event (#11382). Small refs; the bytes are fetched per image
 * from the conversation's attachment route. Empty for every message sent
 * before images could be attached.
 *
 * When this tab is the one that sent the image, its bytes are attached here
 * too, so rebuilding the message list right after a send re-renders the same
 * pixels instead of showing a loading skeleton where the screenshot was.
 */
function attachedImagesFromEvent(userEvent: ChatEventDto): AttachedImageRef[] {
  return attachedImagesFromPayload(userEvent.payload?.images);
}

/**
 * The image refs on a user_message event's payload (`payload.images`), for a
 * reader that holds the payload rather than a whole event: the notetaker
 * chat's reload (#15177).
 */
export function attachedImagesFromPayload(
  // The event payload is an open bag: the value is checked entry by entry.
  attached: unknown
): AttachedImageRef[] {
  if (!Array.isArray(attached)) {
    return [];
  }
  const images: AttachedImageRef[] = [];
  for (const entry of attached) {
    const attachmentId = entry?.attachment_id;
    if (typeof attachmentId !== "string" || !attachmentId) {
      continue;
    }
    images.push({
      attachmentId,
      mediaType:
        typeof entry?.media_type === "string" ? entry.media_type : "image/png",
      name: typeof entry?.name === "string" ? entry.name : attachmentId,
      previewUrl: rememberedImageUrl(attachmentId),
    });
  }
  return images;
}

/**
 * The documents a user message was sent with, as persisted on its
 * user_message event (blob id + the display name resolved server-side at send
 * time). Messages sent before attachments were recorded per-message carry no
 * such record and restore as an empty selection. (#10620)
 */
export function attachedFilesFromEvent(
  userEvent: ChatEventDto
): SelectedFile[] {
  const attached = userEvent.payload?.attached_files;
  if (!Array.isArray(attached)) {
    return [];
  }
  const files: SelectedFile[] = [];
  for (const entry of attached) {
    const blobId = entry?.blob_id;
    if (typeof blobId !== "string" || !blobId) {
      continue;
    }
    files.push({
      id: blobId,
      name: typeof entry?.file_name === "string" ? entry.file_name : blobId,
      isFile: true,
      viaFolder: entry?.via_folder === true,
      ...(Array.isArray(entry?.folder_path)
        ? {
            folderPath: (entry.folder_path as unknown[]).filter(
              (segment): segment is string => typeof segment === "string"
            ),
          }
        : {}),
    });
  }
  return files;
}

/** The records a user_message @-mentioned (#12154), as the send route stored
 * them. Empty for a message with none, or written before the field existed. */
export function referencedEntitiesFromEvent(
  userEvent: ChatEventDto
): ReferencedEntity[] {
  const stored = userEvent.payload?.referenced_entities;
  if (!Array.isArray(stored)) {
    return [];
  }
  return stored.filter(
    (entry): entry is ReferencedEntity =>
      typeof entry?.id === "string" &&
      isEntityKind(entry?.kind) &&
      typeof entry?.name === "string"
  );
}

/** Whether a user_message was written by the host rather than a person (#13752): hidden from
 * the transcript. Every message stored before the field existed is a person's. */
function systemWritten(payload: Record<string, unknown> | undefined): boolean {
  return payload?.origin === "system";
}

/** The document comment a user_message sent to the agent (#13194), or
 * undefined for every other message. */
export function documentCommentFromPayload(
  payload: Record<string, unknown> | undefined
): ChatDocumentComment | undefined {
  const stored = payload?.document_comment as
    | Record<string, unknown>
    | null
    | undefined;
  if (!stored || typeof stored !== "object") return undefined;
  const fields = [
    "artifact_id",
    "title",
    "thread_id",
    "comment_id",
    "body",
    "agent_name",
  ] as const;
  if (!fields.every((field) => typeof stored[field] === "string")) {
    return undefined;
  }
  return {
    artifact_id: String(stored.artifact_id),
    title: String(stored.title),
    thread_id: String(stored.thread_id),
    comment_id: String(stored.comment_id),
    body: String(stored.body),
    agent_name: String(stored.agent_name),
  };
}

/**
 * The folder paths a user message's selection was expanded from, as persisted
 * on its user_message event (#11207). Empty when the sender picked files
 * directly or the event predates the field.
 */
export function attachedFoldersFromEvent(userEvent: ChatEventDto): string[][] {
  const folders = userEvent.payload?.attached_folders;
  if (!Array.isArray(folders)) {
    return [];
  }
  const paths: string[][] = [];
  for (const path of folders) {
    if (Array.isArray(path)) {
      const segments = (path as unknown[]).filter(
        (segment): segment is string => typeof segment === "string"
      );
      if (segments.length > 0) {
        paths.push(segments);
      }
    }
  }
  return paths;
}

/**
 * Rebuild a turn's tool steps by replaying its parsed ChatUpdates — the same
 * `toolCall` / `toolResult` updates the live reducer consumes, so reload and
 * live share the one implementation of the tool-event mapping (#8147,
 * #11155). Steps are matched by tool_call_id, preserving call order. Returns
 * undefined when the turn made no tool calls so the assistant message has no
 * empty step area. Well-formed create_chart / create_diagram calls parse to
 * chart/diagram updates rather than toolCall updates, so they render as
 * inline artifacts, not generic steps.
 */
function toolStepsFromUpdates(updates: ChatUpdate[]): ToolStep[] | undefined {
  const steps: ToolStep[] = [];
  const byId = new Map<string, ToolStep>();
  for (const u of updates) {
    if (u.type === "toolCall") {
      // Clone: the update's step also feeds the segment timeline (buildTurn);
      // the result mutations below must not reach through to it.
      const step: ToolStep = { ...u.step };
      steps.push(step);
      byId.set(step.id, step);
    } else if (u.type === "toolResult") {
      const step = byId.get(u.toolCallId);
      if (step) {
        step.status = u.error !== undefined ? "error" : "done";
        if (u.outputPreview !== undefined) {
          step.outputPreview = u.outputPreview;
        }
        if (u.error !== undefined) {
          step.error = u.error;
        }
        if (u.durationMs !== undefined) {
          step.durationMs = u.durationMs;
        }
      }
    }
  }
  return steps.length > 0 ? steps : undefined;
}

/**
 * Rehydrate a citation source map off the wire (#10664).
 *
 * Every citation from one IODL query used to carry its own copy of that query's
 * `sql` / `columns` / `reasoning` — 579 copies of 3 distinct queries in one real
 * answer — which pushed the frame past the websocket's 1 MB limit, so the answer
 * never arrived live. The backend now sends each distinct query's metadata once
 * in `shared_sources` and points entries at it via `sharedRef`.
 *
 * Resolving here, where the map is already read out of the payload, means
 * everything downstream (segments, BotMessage, MarkDownRenderer) still sees the
 * flat, self-contained entries it always has. An unresolvable ref keeps the
 * entry: losing a citation's SQL is survivable, losing the citation is not.
 */
function resolveSourceMap(
  rawMap: unknown,
  rawShared: unknown
): Record<string, ChatCitationSource[]> | undefined {
  if (!rawMap || typeof rawMap !== "object") return undefined;
  const shared: Record<string, Record<string, unknown>> = rawShared &&
  typeof rawShared === "object"
    ? (rawShared as Record<string, Record<string, unknown>>)
    : {};
  const resolved: Record<string, ChatCitationSource[]> = {};
  for (const [marker, entries] of Object.entries(
    rawMap as Record<string, unknown>
  )) {
    if (!Array.isArray(entries)) continue;
    resolved[marker] = entries.map((raw) => {
      if (!raw || typeof raw !== "object") return raw as ChatCitationSource;
      // `sharedRef` is wire plumbing — drop it rather than leak it to the
      // renderer, which never knew the field existed.
      const { sharedRef, ...entry } = raw as Record<string, unknown> & {
        sharedRef?: string;
      };
      const record = sharedRef ? shared[sharedRef] : undefined;
      return { ...entry, ...(record ?? {}) } as ChatCitationSource;
    });
  }
  return resolved;
}

/**
 * Fold a chronological chat_events stream into the user/assistant message
 * pairs the existing chat UI renders.
 *
 * For each turn:
 *   - emit one user message from the user_message event (queryId = turn_id);
 *   - emit one assistant message whose content concatenates assistant_text
 *     deltas, then is overwritten by the final assistant_text (is_final
 *     = true) if one arrives.
 *
 * System events and tool_call / tool_result events are dropped from the
 * folded view — the foundation pass renders text only. Once tools land
 * we'll surface them inline.
 */
export function foldEventsToMessages(
  events: ChatEventDto[],
  turns: ChatTurnRefDto[] = []
): ChatMessageDto[] {
  // Turn lineage so each message pair links to its branch parent — this is
  // what lets messageTreeUtils rebuild the sibling tree (edit creates a turn
  // whose parent_turn_id is the edited turn's parent). #8147.
  const parentByTurn = new Map<string, string | null>(
    turns.map((t) => [t.turn_id, t.parent_turn_id])
  );

  // Authoritative live status per turn (#10442). Persisted events carry no
  // running/terminal signal a reloaded page can trust (turn_complete is not
  // user-visible and transient deltas aren't persisted), so the backend's
  // chat_turns.status is the only way to tell an in-flight turn from a done
  // one on reload.
  const statusByTurn = new Map<string, ChatTurnRefDto["status"]>(
    turns.map((t) => [t.turn_id, t.status])
  );

  // Bucket events by turn_id, preserving order.
  const turnOrder: string[] = [];
  const eventsByTurn: Record<string, ChatEventDto[]> = {};
  for (const ev of events) {
    if (!ev.turn_id) {
      continue;
    }
    if (!eventsByTurn[ev.turn_id]) {
      eventsByTurn[ev.turn_id] = [];
      turnOrder.push(ev.turn_id);
    }
    eventsByTurn[ev.turn_id].push(ev);
  }

  // A reloaded message's timestamp is when it was recorded, off the persisted
  // event — never the page-load clock, which the evidence footer would then
  // present as the answer's as-of (#11426). Fallback for events predating
  // created_at only.
  const eventInstant = (iso: string | null | undefined): Date =>
    iso ? new Date(iso) : new Date();

  // Turns a later message stopped to send itself (Send now, #11986): the
  // follow-up's user_message names the turn, and that turn renders no
  // interrupted banner — the same as it did live.
  const supersededTurns = new Set<string>();
  for (const ev of events) {
    if (ev.kind === "user_message" && ev.payload?.superseded_turn_id) {
      supersededTurns.add(ev.payload.superseded_turn_id);
    }
  }

  const messages: ChatMessageDto[] = [];
  for (const turnId of turnOrder) {
    const turnEvents = eventsByTurn[turnId];
    const userEvent = turnEvents.find((e) => e.kind === "user_message");
    // A message the host wrote to start the turn (a failed dashboard build the assistant explains,
    // #13752) is not the person's: the turn shows only its answer.
    if (userEvent && !systemWritten(userEvent.payload)) {
      messages.push({
        id: `${turnId}-user`,
        content: String(userEvent.payload?.content || ""),
        isUser: true,
        timestamp: eventInstant(userEvent.created_at),
        queryId: turnId,
        preceding_query_id: parentByTurn.get(turnId) || undefined,
        status: "completed",
        // Re-show the documents this message was sent with (persisted on the
        // user_message event): the chips under the bubble, and — via the last
        // user message — the composer's restored selection. Empty for messages
        // sent before attachments were recorded per-message. (#10620)
        selectedFiles: attachedFilesFromEvent(userEvent),
        // The folders that selection came from, so the composer's restored
        // selection carries the folder chips and the picker re-opens with the
        // folder still selected (#11207).
        selectedFolderPaths: attachedFoldersFromEvent(userEvent),
        // Re-show the tool chip the user picked when this message was sent
        // (persisted on the user_message event). (#8147)
        tool: userEvent.payload?.selected_tool === "web" ? "web" : null,
        // Re-highlight the /skill-name token on reload (#9789).
        skillInvoked: Boolean(userEvent.payload?.skill_id),
        // Re-highlight the message's "@Name" mentions on reload (#12154).
        referencedEntities: referencedEntitiesFromEvent(userEvent),
        // Who sent it, so a shared conversation can name them (#11173). Null
        // for messages recorded before senders were tracked.
        senderUserId: userEvent.payload?.sender_user_id ?? null,
        senderName: userEvent.payload?.sender_name ?? null,
        // Re-show the screenshots this message was sent with (#11382). Refs
        // only — the bytes come from the authorized attachment route, so the
        // transcript renders them without ever putting base64 in the event.
        attachedImages: attachedImagesFromEvent(userEvent),
        // A document comment sent to the agent renders as its card (#13194).
        documentComment: documentCommentFromPayload(userEvent.payload),
      });
    }

    const refStatus = statusByTurn.get(turnId);
    const inFlight = refStatus === "pending" || refStatus === "running";
    const assistantTexts = turnEvents.filter(
      (e) => e.kind === "assistant_text"
    );
    // A turn whose only durable output is a produced file still needs an
    // assistant row to hang the download chips on (#10492).
    const producedOutputs = turnEvents.some((e) => {
      if (e.kind !== "system_event" || e.payload.event !== "outputs_produced") {
        return false;
      }
      const detail =
        e.payload.detail && typeof e.payload.detail === "object"
          ? (e.payload.detail as Record<string, unknown>)
          : undefined;
      return (
        detail !== undefined &&
        Array.isArray(detail.files) &&
        detail.files.length > 0
      );
    });
    // An in-flight turn gets an assistant message even before any text has
    // persisted, so the reloaded page shows it as working and the live
    // stream has a row to attach to (#10442).
    if (assistantTexts.length > 0 || inFlight || producedOutputs) {
      // The answer is the is_final text AFTER the last tool_call — earlier
      // is_final text is the model's process narration, not the answer (U14,
      // #10715). find() previously returned the FIRST is_final (the opening
      // "I'll look up…" preamble) and its empty source_map. A turn with no
      // tool_call is unchanged: all its text is the answer.
      const lastToolIdx = turnEvents.reduce(
        (acc, e, i) => (e.kind === "tool_call" ? i : acc),
        -1
      );
      const answerTexts =
        lastToolIdx === -1
          ? assistantTexts
          : turnEvents.filter(
              (e, i) => e.kind === "assistant_text" && i > lastToolIdx
            );
      // Prefer the final (is_final=true) answer text if present; otherwise
      // concat the deltas.
      const finalText = answerTexts
        .slice()
        .reverse()
        .find((e) => e.payload?.is_final);
      const content = finalText
        ? String(finalText.payload?.content || "")
        : answerTexts.map((e) => String(e.payload?.content || "")).join("");
      // Legacy citations persisted on the final text event, keyed by the
      // bracketed `[NNNNN.M]` marker. Backfill is de-duplicated the same way
      // the live frame is, so it resolves through the same helper — a reloaded
      // answer must render identically to the one that streamed in (#10664).
      const reloadedSourceMap = resolveSourceMap(
        finalText?.payload?.source_map,
        finalText?.payload?.shared_sources
      );
      const cancelEvent = turnEvents.find(
        (e) =>
          e.kind === "system_event" && e.payload?.event === "turn_cancelled"
      );
      const errorEvent = turnEvents.find((e) => e.kind === "error");
      let status: ChatMessageDto["status"] = "completed";
      if (inFlight) {
        status = content ? "streaming" : "thinking";
      } else if (cancelEvent || refStatus === "cancelled") {
        status = "canceled";
      } else if (
        refStatus === "failed" ||
        // A turn that timed out and whose salvage then delivered an answer
        // is written ``complete`` over its ``turn_timed_out`` error event
        // (#10903); the row's status wins, so a reload shows the answer the
        // way the live stream ended, not as a failure. The error event alone
        // still decides for payloads that predate the row status (#10442).
        (errorEvent && refStatus !== "complete")
      ) {
        status = "failed";
      }
      // Rebuild the agentic block-timeline (#10242) by replaying this turn's
      // persisted, seq-ordered events through the same builder the live stream
      // uses. Transient deltas (thinking, non-final text) aren't persisted, so
      // reload reconstructs the durable text + tool + chart segments in order.
      // Turn-level status is pinned from the computed message status (the
      // persisted set may omit a UI-visible terminal event).
      // conversation_id is carried so events keyed on the conversation
      // (produced-file downloads, #10492) rebuild on reload, not just live.
      const turnUpdates = turnEvents
        .map((e) =>
          parseChatEvent(
            chatEventFrame(e, { turnId, conversationId: e.conversation_id })
          )
        )
        .filter((u): u is ChatUpdate => u !== null);
      const builtTurn = buildTurn(turnUpdates);
      // A live turn stays running; anything else closes out through the same
      // terminal path a live turn-complete takes (closeOutTurn), so a trailing
      // tool_call with no persisted tool_result renders done, not spinning.
      const closedTurn: TurnState = inFlight
        ? { ...builtTurn, status: "running" }
        : closeOutTurn(
            builtTurn,
            status === "canceled"
              ? "canceled"
              : status === "failed"
                ? "failed"
                : "completed"
          );
      // A build ending recorded after its turn ended stands at the end of the
      // transcript, as it did live; one recorded while the turn was open
      // stays in the turn's card (dashboard-assistant-polish requirement 10).
      const turn: TurnState = inFlight
        ? closedTurn
        : lateBuildEndingsOf(turnEvents).reduce(
            (acc, buildId) => ({
              ...acc,
              segments: markBuildEndedAfterTurn(acc.segments, buildId),
            }),
            closedTurn
          );

      messages.push({
        id: `${turnId}-assistant`,
        content,
        isUser: false,
        // The turn's last persisted event is when the answer finished — the
        // as-of the evidence footer states.
        timestamp: eventInstant(turnEvents[turnEvents.length - 1]?.created_at),
        // When the turn started (its first event, the user's message) and,
        // once it is over, when it ended: the trace fold's "Worked 3m 41s"
        // (#12060), the same instants a live turn's clock read.
        thinkingTimes: {
          start: eventInstant(turnEvents[0]?.created_at),
          ...(inFlight
            ? {}
            : {
                end: eventInstant(
                  turnEvents[turnEvents.length - 1]?.created_at
                ),
              }),
        },
        queryId: turnId,
        preceding_query_id: parentByTurn.get(turnId) || undefined,
        status,
        ...(status === "canceled" && supersededTurns.has(turnId)
          ? { supersededByFollowUp: true }
          : {}),
        selectedFiles: [],
        toolSteps: toolStepsFromUpdates(turnUpdates),
        chatSourceMap: reloadedSourceMap,
        charts: chartsFromUpdates(turnUpdates),
        turn,
      });
    }
  }
  return messages;
}

/** Whether a stored event is a dashboard build's ending. */
const isBuildEnding = (event: ChatEventDto): boolean =>
  event.kind === "system_event" &&
  event.payload?.event === "dashboard_build_ended";

/**
 * The builds whose endings a turn's stored events record after the turn ended. The turn row
 * carries no end time, so the turn ended at its last event that is not a build ending. An ending
 * or a turn with no recorded time is not known to be late, and stays in the turn's card.
 */
export function lateBuildEndingsOf(turnEvents: ChatEventDto[]): string[] {
  const ended = turnEvents
    .filter((e) => !isBuildEnding(e) && e.created_at)
    .reduce<number | null>((last, e) => {
      const at = Date.parse(e.created_at as string);
      return Number.isNaN(at) ? last : Math.max(last ?? at, at);
    }, null);
  if (ended === null) return [];
  return turnEvents.flatMap((e) => {
    if (!isBuildEnding(e) || !e.created_at) return [];
    const at = Date.parse(e.created_at);
    const detail = e.payload.detail as { build?: unknown } | undefined;
    const build = (e.payload.build ?? detail?.build) as
      | { id?: unknown }
      | undefined;
    return !Number.isNaN(at) && at > ended && typeof build?.id === "string"
      ? [build.id]
      : [];
  });
}

/** The one ``CHAT_EVENT`` envelope ``parseChatEvent`` consumes. */
export interface ChatEventFrame {
  message_type: "CHAT_EVENT";
  message_data: {
    kind: string;
    event_id: string | undefined;
    conversation_id: string | undefined;
    turn_id: string | undefined;
    payload: Record<string, unknown>;
    /** When a persisted row was recorded; absent on a live frame. */
    created_at?: string | null;
  };
}

/**
 * Restate one chat event — a persisted row or a rowless streaming frame — as
 * the ``CHAT_EVENT`` envelope ``parseChatEvent`` consumes. This used to be
 * hand-built at each call site (the agents websocket handler, the agents
 * transcript fold, chat's reload fold) with drifting field sets; the frame is
 * now always the full union the parser reads: ``kind``/``turn_id``/``payload``
 * that every update keys on, ``event_id`` (read only by clarifying_question),
 * and ``conversation_id`` (read only by outputs_produced). Extra fields are
 * inert — the parser reads by name — so the union is safe everywhere.
 */
export function chatEventFrame(
  event: {
    kind: string;
    payload: Record<string, unknown>;
    event_id?: string;
    created_at?: string | null;
  },
  ids: { turnId: string | undefined; conversationId: string | undefined }
): ChatEventFrame {
  return {
    message_type: "CHAT_EVENT",
    message_data: {
      kind: event.kind,
      event_id: event.event_id,
      conversation_id: ids.conversationId,
      turn_id: ids.turnId,
      payload: event.payload,
      created_at: event.created_at,
    },
  };
}

/** The non-empty strings in a system event detail's list field: the names a
 *  turn notice shows (memory subjects, research cut short). Anything else in
 *  the list, or in the detail, is never read. */
function detailNames(detail: unknown, key: string): string[] {
  if (!detail || typeof detail !== "object") return [];
  const value = (detail as Record<string, unknown>)[key];
  return Array.isArray(value)
    ? (value as unknown[]).filter(
        (s): s is string => typeof s === "string" && s.trim() !== ""
      )
    : [];
}

/** The refused files of a `memory_updated` detail (#11402): each entry
 *  needs a string path and a string reason; anything else is dropped. */
function detailRefused(detail: unknown): RefusedAgentFile[] {
  if (!detail || typeof detail !== "object") return [];
  const value = (detail as Record<string, unknown>).refused;
  if (!Array.isArray(value)) return [];
  const refused: RefusedAgentFile[] = [];
  for (const entry of value as unknown[]) {
    if (!entry || typeof entry !== "object") continue;
    const { path, reason } = entry as Record<string, unknown>;
    if (typeof path === "string" && path && typeof reason === "string") {
      refused.push({ path, reason });
    }
  }
  return refused;
}

/**
 * Map a single CHAT_EVENT websocket frame to a ChatUpdate (or null if it
 * doesn't have a UI-visible mapping yet — e.g. system_event bookkeeping
 * or tool_call/tool_result in the foundation pass).
 */
export function parseChatEvent(message: unknown): ChatUpdate | null {
  const typed = message as any;
  if (!typed || typed.message_type !== "CHAT_EVENT") {
    return null;
  }
  const data = typed.message_data || {};
  const kind: string | undefined = data.kind;
  const turnId: string | undefined = data.turn_id || undefined;
  const payload = data.payload || {};
  // Set when a dispatched subagent produced this event (#10242 / #8774): the id
  // of the Task call that spawned it, so the timeline nests it under that call.
  const parentToolCallId: string | undefined =
    typeof payload.parent_tool_call_id === "string" &&
    payload.parent_tool_call_id
      ? payload.parent_tool_call_id
      : undefined;
  // Conversation-scoped events carry no turn_id — handle them before the
  // turn-id guard below.
  if (kind === "conversation_titled") {
    return { type: "title", title: String(payload.title || "") };
  }
  // Streaming-only keep-alive so a long model step doesn't idle the socket.
  // Carries no fields, belongs to no turn, and has nothing to render.
  if (kind === "heartbeat") {
    return null;
  }
  if (!kind || !turnId) {
    return null;
  }
  switch (kind) {
    case "user_message":
      // A document comment sent to the agent (#13194) starts a turn this page
      // did not send: the page reads the conversation again to show the
      // comment's card and follow the turn.
      // So does the host's own message (#13752): the page reads the turn it started.
      if (documentCommentFromPayload(payload) || systemWritten(payload)) {
        return { type: "externalTurn", messageId: turnId };
      }
      // The user's own turn content: the sender already rendered its
      // message optimistically.
      return null;
    case "clarifying_answer":
      // An answer opens a new turn rather than belonging to this one.
      return null;

    case "clarifying_question": {
      // The row's own id, needed because the answer is posted against it. Only
      // the projects wire carries it — chat's agent never asks.
      const eventId = typeof data.event_id === "string" ? data.event_id : "";
      const question =
        typeof payload.question === "string" ? payload.question : "";
      if (!eventId || !question) {
        return null;
      }
      return {
        type: "clarifyingQuestion",
        messageId: turnId,
        eventId,
        question,
        toolUseId:
          typeof payload.tool_use_id === "string" ? payload.tool_use_id : null,
      };
    }

    case "assistant_text":
      if (payload.is_final) {
        // ``contentWithCitations`` -> SET_FINAL_ANSWER, which also clears
        // currentThinking and flips status to ``completed``. The final event
        // carries the turn's legacy citation source_map, keyed by the bracketed
        // `[NNNNN.M]` marker; the renderer resolves those markers against it.
        const sourceMap = resolveSourceMap(
          payload.source_map,
          payload.shared_sources
        );
        return {
          type: "contentWithCitations",
          messageId: turnId,
          text: String(payload.content || ""),
          chatSourceMap: sourceMap,
          parentToolCallId,
          eventId:
            typeof data.event_id === "string" ? data.event_id : undefined,
        };
      }
      return {
        type: "streaming",
        messageId: turnId,
        text: String(payload.content || ""),
        parentToolCallId,
      };

    case "assistant_thinking":
      return {
        type: "thinking",
        messageId: turnId,
        text: String(payload.content || ""),
        parentToolCallId,
      };

    case "tool_call": {
      // Migrated projects events (#11037) carry the legacy vocabulary --
      // {name, tool_use_id} -- verbatim, because the migration copies payloads
      // without translation, while live unified events carry
      // {tool_name, tool_call_id}. Fall back so migrated conversations keep
      // their tool identity: the label, the call/result binding, and a stable
      // non-empty segment key (an empty id collapses every step onto one
      // React key).
      const toolName = String(payload.tool_name || payload.name || "tool");
      const toolCallId = String(
        payload.tool_call_id || payload.tool_use_id || ""
      );
      // create_diagram surfaces as an inline diagram, not a generic tool step:
      // the Mermaid source rides in the call's input (#10250).
      if (toolName === DIAGRAM_TOOL_NAME) {
        const input =
          payload.input && typeof payload.input === "object"
            ? (payload.input as Record<string, any>)
            : {};
        const source =
          typeof input.diagram === "string" && input.diagram !== ""
            ? input.diagram
            : null;
        if (source) {
          return {
            type: "diagram",
            messageId: turnId,
            diagramId: toolCallId,
            diagram: source,
            title: typeof input.title === "string" ? input.title : undefined,
            sourceIds: chartSourceIds(input),
            parentToolCallId,
          };
        }
        // Malformed (no source) — fall through to a normal step so the call is
        // still visible rather than silently dropped.
      }
      // create_chart surfaces as an inline chart, not a generic tool step: the
      // Chart.js spec rides in the call's input (#8774).
      if (toolName === CHART_TOOL_NAME) {
        const input =
          payload.input && typeof payload.input === "object"
            ? (payload.input as Record<string, any>)
            : {};
        const spec =
          input.spec && typeof input.spec === "object" ? input.spec : null;
        if (spec) {
          return {
            type: "chart",
            messageId: turnId,
            chartId: toolCallId,
            spec: spec as Record<string, any>,
            title: typeof input.title === "string" ? input.title : undefined,
            sourceIds: chartSourceIds(input),
            parentToolCallId,
          };
        }
        // Malformed (no spec) — fall through to a normal step so the call is
        // still visible rather than silently dropped.
      }
      // Start a running step for this tool call (#8147 intermediate steps).
      return {
        type: "toolCall",
        messageId: turnId,
        step: {
          id: toolCallId,
          toolName,
          input:
            payload.input && typeof payload.input === "object"
              ? (payload.input as Record<string, any>)
              : undefined,
          status: "running",
          // Backend-resolved readable label (names the document a read-tool
          // acts on); the timeline prefers it over deriveToolLabel. (#10373)
          displayVerb:
            typeof payload.display_verb === "string"
              ? payload.display_verb
              : undefined,
        },
        parentToolCallId,
      };
    }

    case "tool_result":
      // Flip the matching step to done/error. Same migrated-vocabulary
      // fallback as tool_call above -- a result must bind to its call under
      // either key set.
      return {
        type: "toolResult",
        messageId: turnId,
        toolCallId: String(payload.tool_call_id || payload.tool_use_id || ""),
        outputPreview:
          payload.output_preview != null
            ? String(payload.output_preview)
            : undefined,
        data:
          payload.data !== null && typeof payload.data === "object"
            ? (payload.data as Record<string, unknown> | unknown[])
            : undefined,
        outputContent:
          typeof payload.content === "string" ? payload.content : undefined,
        error: payload.error != null ? String(payload.error) : undefined,
        durationMs:
          typeof payload.duration_ms === "number"
            ? payload.duration_ms
            : undefined,
        parentToolCallId,
      };

    case "system_event": {
      const event = payload.event;
      if (event === "turn_complete") {
        // The terminal event's detail carries the sender's resolved status
        // plus that turn's cumulative totals (see `_turn_complete_frame`).
        // Chat renders neither, but they are the same event, so they are
        // parsed once here rather than re-read downstream.
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const totals: Record<string, number> = {};
        for (const [key, value] of Object.entries(detail)) {
          if (key.startsWith("total_") && typeof value === "number") {
            totals[key] = value;
          }
        }
        return {
          type: "status",
          messageId: turnId,
          status: "completed",
          sessionStatus:
            typeof detail.session_status === "string"
              ? detail.session_status
              : undefined,
          totals: Object.keys(totals).length > 0 ? totals : undefined,
        };
      }
      if (event === "usage_snapshot") {
        // Cumulative accounting pushed repeatedly during a turn. Every field
        // is a plain integer in `detail`; a missing one means "nothing
        // counted yet", so it reads as 0 rather than as unknown.
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const count = (key: string): number => {
          const raw = detail[key];
          return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
        };
        return {
          type: "usage",
          messageId: turnId,
          usage: {
            input_tokens: count("input_tokens"),
            output_tokens: count("output_tokens"),
            cache_read_input_tokens: count("cache_read_input_tokens"),
            cache_creation_input_tokens: count("cache_creation_input_tokens"),
            context_used_tokens: count("context_used_tokens"),
            context_window_tokens: count("context_window_tokens"),
            context_compaction_tokens: count("context_compaction_tokens"),
          },
        };
      }
      if (event === "turn_cancelled") {
        return { type: "status", messageId: turnId, status: "canceled" };
      }
      if (event === "memory_updated") {
        // Memory changed for this turn (#12402): a memory tool wrote during
        // it, or the post-turn curation pass wrote after turn_complete. Same
        // shape live and on reload; the detail carries the subjects' names
        // and never the memory's content.
        // A named agent's notice also names the brief and skill files the
        // turn wrote back and those it refused to save (#11402).
        const subjects = detailNames(payload.detail, "subjects");
        const files = detailNames(payload.detail, "files");
        const refused = detailRefused(payload.detail);
        if (!turnId) {
          return null;
        }
        return {
          type: "memoryUpdated",
          messageId: turnId,
          subjects,
          files,
          refused,
        };
      }
      if (event === "dashboard_build_ended") {
        // A dashboard build landed or failed (#13380). Stored and the same
        // live and on reload; it folds into the turn that asked for the build.
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const raw = (payload.build ?? detail.build) as
          | Record<string, unknown>
          | undefined;
        if (
          !turnId ||
          !raw ||
          typeof raw !== "object" ||
          typeof raw.id !== "string" ||
          (raw.status !== "landed" &&
            raw.status !== "failed" &&
            raw.status !== "paused")
        ) {
          return null;
        }
        return {
          type: "dashboardBuildEnded",
          messageId: turnId,
          build: {
            id: raw.id,
            title: typeof raw.title === "string" ? raw.title : "",
            status: raw.status,
            widgetId: typeof raw.widget_id === "string" ? raw.widget_id : null,
            reason: typeof raw.reason === "string" ? raw.reason : null,
            copyOf: typeof raw.copy_of === "string" ? raw.copy_of : null,
            // A stored ending carries when it was recorded; a live one is
            // dated as it arrives.
            at:
              typeof data.created_at === "string"
                ? data.created_at
                : new Date().toISOString(),
          },
        };
      }
      if (event === "turn_notice") {
        // A builder turn's extra pass, or its closing line when no pass
        // worked (#11849). Persisted, so a reload shows why a second reply
        // follows the first. Unknown codes render nothing.
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const code = detail.code;
        if (
          !turnId ||
          (code !== "builder_retry" && code !== "builder_gave_up")
        ) {
          return null;
        }
        return { type: "turnNotice", messageId: turnId, code };
      }
      if (event === "outputs_produced") {
        // Files the agent built and the runtime uploaded (#10492). Same shape
        // live and on reload, so the download chips survive a page load.
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const files = Array.isArray(detail.files)
          ? (detail.files as unknown[]).filter(
              (f): f is string => typeof f === "string" && f.trim() !== ""
            )
          : [];
        const conversationId =
          typeof data.conversation_id === "string" ? data.conversation_id : "";
        if (!files.length || !conversationId || !turnId) {
          return null;
        }
        // Announced globally so surfaces hosting the chat (the artifact chat,
        // #11523) can react — e.g. auto-open the produced file in the viewer
        // so a revised document refreshes in place instead of going stale.
        if (typeof window !== "undefined") {
          window.dispatchEvent(
            new CustomEvent("chatOutputsProduced", {
              detail: { conversationId, turnId, files },
            })
          );
        }
        return { type: "outputs", messageId: turnId, conversationId, files };
      }
      if (event === "thinking_note") {
        // One sanitized excerpt of a completed thinking run (#10396).
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const text = typeof detail.text === "string" ? detail.text.trim() : "";
        if (!text) {
          return null;
        }
        return {
          type: "thinkingNote",
          messageId: turnId,
          text,
          block: typeof detail.block === "number" ? detail.block : 0,
        };
      }
      if (event === "status_gist") {
        // A work-block trace update (#10382): the block's model-written
        // header plus per-tool activity lines, anchored to its block index.
        // Same shape live and on reload.
        const detail =
          payload.detail && typeof payload.detail === "object"
            ? (payload.detail as Record<string, unknown>)
            : {};
        const text = typeof detail.text === "string" ? detail.text.trim() : "";
        if (!text) {
          return null;
        }
        const steps = Array.isArray(detail.steps)
          ? (detail.steps as unknown[]).filter(
              (s): s is string => typeof s === "string" && s.trim() !== ""
            )
          : undefined;
        const block = typeof detail.block === "number" ? detail.block : 0;
        return {
          type: "statusGist",
          messageId: turnId,
          text,
          block,
          steps,
          stepsBlock:
            typeof detail.steps_block === "number" ? detail.steps_block : block,
        };
      }
      return null;
    }

    case "error":
      return {
        type: "error",
        messageId: turnId,
        error: String(payload.message || "Agent encountered an error."),
        // The server's code, which a host reads to act on a refusal it knows (polish-8 R2).
        ...(typeof payload.code === "string" && payload.code
          ? { code: payload.code }
          : {}),
      };

    default:
      return null;
  }
}
